"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { MapPin, ArrowRight, Maximize2, Calendar, Ruler, BrickWall } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
const PROJECTS = [{
  id: "p1",
  title: "Lakeside Estate Grounds",
  category: "Landscaping",
  location: "Coral Gables, FL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/elyon_landscaping_lakeside.png",
  description: "A comprehensive seasonal landscaping and irrigation management project for a 2-acre private estate. Our team maintains the pristine appearance of the grounds through precision mowing, organic fertilization, and custom floral rotations.",
  specs: {
    area: "8,000 sqft",
    year: "2024",
    architect: "GreenScape Designs"
  },
  materials: ["Organic Mulch", "Native Flora", "Smart Irrigation"],
  heightClass: "h-96"
}, {
  id: "p2",
  title: "Skyline Office Tower",
  category: "Cleaning",
  location: "Downtown Miami, FL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/elyon_cleaning_skyline.png",
  description: "Daily janitorial services and specialized floor care for a 40-story corporate headquarters. We utilize hospital-grade sanitization protocols and eco-friendly cleaning agents to ensure a healthy, productive environment for over 2,000 employees.",
  specs: {
    area: "45,000 sqft",
    year: "Ongoing",
    architect: "Elyon Janitorial Team"
  },
  materials: ["Eco-friendly Cleaners", "HEPA Filtration", "Microfiber Tech"],
  heightClass: "h-[500px]"
}, {
  id: "p3",
  title: "Coastal Manor Refresh",
  category: "Painting",
  location: "Palm Beach, FL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/elyon_painting_coastal.png",
  description: "Full exterior painting and restorative coating for a luxury seaside residence. This project involved meticulous surface preparation and the application of high-performance, salt-resistant coatings designed to withstand harsh oceanic elements.",
  specs: {
    area: "5,500 sqft",
    year: "2023",
    architect: "Elyon Painting Div."
  },
  materials: ["Elastomeric Paint", "Zinc Primers", "UV Blockers"],
  heightClass: "h-80"
}, {
  id: "p4",
  title: "Riverwalk Plaza Wash",
  category: "Power Washing",
  location: "Fort Lauderdale, FL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/elyon_powerwash_riverwalk.png",
  description: "High-pressure restorative cleaning of stone walkways and building facades in a high-traffic commercial plaza. Our soft-wash systems removed years of environmental buildup without damaging the delicate masonry surfaces.",
  specs: {
    area: "12,000 sqft",
    year: "2024",
    architect: "Maintenance Pros"
  },
  materials: ["Biodegradable Degreasers", "Soft-Wash Systems", "Surface Sealants"],
  heightClass: "h-64"
}, {
  id: "p5",
  title: "Modern Loft Sanitation",
  category: "Cleaning",
  location: "Wynwood, Miami",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/elyon_cleaning_loft.png",
  description: "Deep post-construction cleaning and sanitization for luxury residential units. We focused on dust extraction from industrial ceilings and polishing of exposed concrete floors to deliver a move-in ready sanctuary.",
  specs: {
    area: "2,200 sqft",
    year: "2024",
    architect: "Urban Living"
  },
  materials: ["Hospital-grade Disinfectant", "Steam Cleaners", "Industrial Vacuums"],
  heightClass: "h-[400px]"
}, {
  id: "p6",
  title: "Palm Grove Revitalization",
  category: "Landscaping",
  location: "Naples, FL",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/elyon_landscaping_palmgrove.png",
  description: "Hardscape restoration and tropical plant installation for a boutique resort courtyard. The project included cleaning of existing pavers, resetting stone borders, and introducing a low-maintenance, high-impact tropical foliage scheme.",
  specs: {
    area: "3,500 sqft",
    year: "2023",
    architect: "Nature's Edge"
  },
  materials: ["Paver Sealant", "Specimen Palms", "Low-voltage Lighting"],
  heightClass: "h-96"
}];
export default function OurBuiltLegacy() {
  const [filter, setFilter] = useState("All");
  const filteredProjects = filter === "All" ? PROJECTS : PROJECTS.filter(p => p.category === filter);
  return <section data-section-id="36371" id="our-craftsmanship-in-action" className="bg-background py-24 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mb-16 flex flex-col items-center text-center">
          <h2 className="font-sans text-4xl font-black tracking-tighter text-foreground sm:text-6xl uppercase">
            Our Craftsmanship in Action
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground font-medium">
            Explore our portfolio of premium facility maintenance projects, showcasing our 
            commitment to reliability and top-tier property care for residential and 
            commercial clients across the region.
          </p>

          
          <div className="mt-10">
            <ToggleGroup type="single" value={filter} onValueChange={value => {
            if (value) setFilter(value);
          }} className="flex flex-wrap justify-center gap-2">
              {["All", "Landscaping", "Painting", "Cleaning", "Power Washing"].map((category, i) => <ToggleGroupItem key={category} value={category} data-index={i} className="rounded-lg border border-border px-6 py-2 text-sm font-bold tracking-tight transition-all hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground shadow-sm">
                    {category}
                  </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        
        <div className="columns-1 gap-6 space-y-6 md:columns-2 lg:columns-3">
          {filteredProjects.map((project, i) => <div key={project.id} className="break-inside-avoid" data-index={i}>
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative cursor-pointer overflow-hidden rounded-xl bg-card shadow-xl shadow-primary/5 transition-all hover:shadow-2xl hover:shadow-primary/10">
                    
                    <div className={cn("relative w-full overflow-hidden", project.heightClass)}>
                      <img src={project.image} alt={project.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      
                      
                      <div className="absolute inset-0 bg-secondary/60 opacity-0 backdrop-blur-[2px] transition-opacity duration-300 group-hover:opacity-100" />
                      
                      
                      <div className="absolute bottom-0 left-0 w-full p-6 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="flex items-center gap-2 text-primary">
                          <MapPin className="h-4 w-4" />
                          <span className="text-sm font-bold tracking-widest uppercase">
                            {project.location}
                          </span>
                        </div>
                        <h3 className="mt-1 font-sans text-2xl font-black text-white tracking-tight">
                          {project.title}
                        </h3>
                        <div className="mt-3 flex items-center gap-2 text-xs font-bold text-white/90">
                          <span className="uppercase tracking-widest">View Details</span>
                          <ArrowRight className="h-3 w-3" />
                        </div>
                      </div>

                      
                      <div className="absolute top-4 right-4 rounded-lg bg-background/95 px-3 py-1.5 text-xs font-bold text-primary shadow-lg backdrop-blur-md uppercase tracking-wider">
                        {project.category}
                      </div>
                    </div>
                  </div>
                </DialogTrigger>

                
                <DialogContent className="max-w-4xl overflow-hidden p-0 sm:max-w-5xl rounded-2xl shadow-2xl">
                  <div className="grid gap-0 md:grid-cols-2">
                    
                    <div className="relative h-64 w-full bg-muted md:h-full">
                      <img src={project.image} alt={project.title} className="h-full w-full object-cover" />
                      <div className="absolute top-4 left-4">
                         <span className="inline-flex items-center rounded-lg bg-primary/95 px-4 py-1.5 text-xs font-bold text-primary-foreground shadow-xl backdrop-blur-sm uppercase tracking-widest">
                            {project.category}
                         </span>
                      </div>
                    </div>

                    
                    <div className="flex flex-col p-6 md:p-10 bg-background">
                      <DialogHeader className="mb-6 text-left">
                        <div className="mb-2 flex items-center gap-2 text-primary">
                          <MapPin className="h-4 w-4" />
                          <span className="text-xs font-bold uppercase tracking-widest">
                            {project.location}
                          </span>
                        </div>
                        <DialogTitle className="font-sans text-3xl font-black text-secondary md:text-5xl tracking-tighter leading-none">
                          {project.title}
                        </DialogTitle>
                        <DialogDescription className="mt-6 text-lg leading-relaxed text-muted-foreground font-medium">
                          {project.description}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="mt-auto space-y-8">
                        
                        <div className="grid grid-cols-2 gap-4 border-y border-border py-6">
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                              <Ruler className="h-3 w-3" /> Area
                            </span>
                            <span className="text-sm font-bold text-secondary">
                              {project.specs.area}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                              <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate> Status
                            </span>
                            <span className="text-sm font-bold text-secondary">
                              {project.specs.year}
                            </span>
                          </div>
                          <div className="col-span-2 flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                              <BrickWall className="h-3 w-3" /> Focus Areas
                            </span>
                            <span className="text-sm font-bold text-secondary">
                              {project.materials.join(", ")}
                            </span>
                          </div>
                        </div>

                        
                        <div className="flex justify-end gap-3">
                          <DialogClose asChild>
                            <Button variant="outline" className="rounded-lg px-8 font-bold border-2">Close</Button>
                          </DialogClose>
                          <Link to="#initiate-your-dominance">
                            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 font-bold shadow-lg shadow-primary/20">
                              Get a Free Quote
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}